import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, History, Loader2, Lock, Pencil, RotateCcw, X } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useToast } from '@/shared/components/ui/use-toast';
import { InlineMarkdownField, MarkdownViewToggle } from '@/shared/components/markdown/InlineMarkdownField';
import { INITIAL_VIEW, lengthStatus, type MarkdownView } from '@/shared/components/markdown/markdownFieldView';
import { useIsAdmin } from '@/shared/hooks/use-admin-validation';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';
import { cn } from '@/infrastructure/lib/utils';
import { MagicFillFieldButton } from '@/shared/components/form/MagicFillFieldButton';
import { formatDateTimeBR } from '@/shared/utils/datetime';
import { BrainService } from '@/features/Knowledge/services/brainService';
import { SoulHistoryList } from './SoulHistoryList';

/** Mesmo teto do api (AB#86817, AC2): a Soul vai para toda conversa. */
const SOUL_MAX_LENGTH = 4000;

/** Altura fixa do campo no formulário (o card tem 420px; a barra, ~45px). */
const SOUL_EDITOR_HEIGHT = 372;

interface SoulPanelProps {
  workspaceId: string;
  /** Os outros campos do formulário (nome, tipo), como contexto do Magic Fill. */
  getFormValues?: () => Record<string, unknown>;
}

/**
 * A Soul — quem o workspace é (AB#86817, AC1/AC2). É a descrição do workspace e
 * ocupa o lugar dela no formulário da aba Geral das configurações. Salva sozinha,
 * pelos endpoints da Soul (com histórico), não pelo botão do formulário; só o
 * admin vê o lápis e salva. Os botões são `type="button"` porque vivem dentro do
 * `<form>` do WorkspaceForm.
 */
export const SoulPanel = ({ workspaceId, getFormValues }: SoulPanelProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAdmin } = useIsAdmin();
  const { data: soul, isLoading } = useQuery({
    queryKey: ['soul', workspaceId],
    queryFn: () => BrainService.getSoul(workspaceId!),
    enabled: !!workspaceId,
  });
  const [saving, setSaving] = useState(false);

  const saved = soul?.content ?? '';
  const [draft, setDraft] = useState(saved);
  const [view, setView] = useState<MarkdownView>(INITIAL_VIEW);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [restoring, setRestoring] = useState(false);

  const { data: versions = [], isLoading: versionsLoading } = useQuery({
    queryKey: ['soul-versions', workspaceId],
    queryFn: () => BrainService.getSoulVersions(workspaceId!),
    enabled: historyOpen && !!workspaceId,
  });

  useEffect(() => {
    setDraft(saved);
  }, [saved]);

  const dirty = draft !== saved;
  const status = lengthStatus(draft, SOUL_MAX_LENGTH);
  const shownVersion = selectedVersion !== null ? versions.find((v) => v.version === selectedVersion) : undefined;

  /**
   * A Soul é a `description` do workspace: depois de salvar ou restaurar, o workspace
   * em cache (configurações, listas e o workspace ativo do contexto) fica defasado.
   * `user-products` é a lista do WorkspaceContext, que ao recarregar atualiza o
   * workspace ativo — sem o reloadUserProducts, que cobre a tela com o loading.
   */
  const refreshAfterChange = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['soul', workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['soul-versions', workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['current-organization-products'] }),
      queryClient.invalidateQueries({ queryKey: ['user-products'] }),
    ]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await BrainService.saveSoul(workspaceId, draft);
      await refreshAfterChange();
      setView('rendered');
      setEditing(false);
      toast({ title: t('knowledge.soul.saved') });
    } catch (error) {
      toast({
        title: t('knowledge.soul.saveError'),
        description: resolveApiErrorMessage(error, t),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const restore = async (version: number) => {
    setRestoring(true);
    try {
      await BrainService.restoreSoulVersion(workspaceId, version);
      await refreshAfterChange();
      setSelectedVersion(null);
      toast({ title: t('knowledge.soul.restored', { version }) });
    } catch (error) {
      toast({
        title: t('knowledge.soul.restoreError'),
        description: resolveApiErrorMessage(error, t),
        variant: 'destructive',
      });
    } finally {
      setRestoring(false);
    }
  };

  const startEditing = () => {
    setDraft(saved);
    setSelectedVersion(null);
    setHistoryOpen(false);
    setView('code');
    setEditing(true);
  };

  // O texto gerado abre a edição: o admin revisa e só então salva com o check.
  const applyGenerated = (value: unknown) => {
    setDraft(String(value ?? '').slice(0, SOUL_MAX_LENGTH));
    setSelectedVersion(null);
    setHistoryOpen(false);
    setView('code');
    setEditing(true);
  };

  const cancelEditing = () => {
    setDraft(saved);
    setView('rendered');
    setEditing(false);
  };

  const lastChange = soul && 'author' in soul ? soul : null;
  const shownText = editing ? draft : saved;

  return (
    <Card className="flex h-[420px] flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b bg-muted/30 px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground/70">
            {t('knowledge.soul.title')}
          </span>
          {lastChange?.version ? (
            <>
              <span className="text-xs text-muted-foreground/30">·</span>
              <span className="truncate text-[11px] text-muted-foreground">
                {t('knowledge.soul.version', { version: lastChange.version })}
                {lastChange.created_at ? ` · ${formatDateTimeBR(lastChange.created_at)}` : ''}
                {lastChange.author
                  ? ` · ${t('knowledge.soul.by', { name: lastChange.author.full_name || lastChange.author.email })}`
                  : ''}
              </span>
            </>
          ) : null}
          {!isAdmin && (
            <Badge variant="secondary" className="gap-1 text-[10px]">
              <Lock className="h-3 w-3" />
              {t('knowledge.soul.readOnly')}
            </Badge>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {isAdmin && !shownVersion && (
            <MagicFillFieldButton
              formType="product"
              fieldKey="description"
              getCurrentValues={() => ({ ...(getFormValues?.() ?? {}), description: editing ? draft : saved })}
              metadata={{ workspaceId }}
              // O botão nasce absoluto (para morar dentro de um input); na barra ele é mais um ícone.
              className="!static !translate-y-0 flex h-8 w-8 items-center justify-center"
              onApplySuggestion={applyGenerated}
            />
          )}
          {!editing && (
            <ToolbarButton
              label={t('knowledge.soul.history')}
              variant={historyOpen ? 'secondary' : 'ghost'}
              onClick={() => {
                setHistoryOpen(!historyOpen);
                setSelectedVersion(null);
              }}
            >
              <History className="h-3.5 w-3.5" />
            </ToolbarButton>
          )}

          {editing && <MarkdownViewToggle view={view} onChange={setView} className="h-8 w-8" />}

          {isAdmin && !editing && !shownVersion && (
            <ToolbarButton label={t('knowledge.soul.edit')} onClick={startEditing}>
              <Pencil className="h-3.5 w-3.5" />
            </ToolbarButton>
          )}

          {isAdmin && editing && (
            <>
              <ToolbarButton label={t('knowledge.soul.discard')} onClick={cancelEditing} disabled={saving}>
                <X className="h-3.5 w-3.5" />
              </ToolbarButton>
              <ToolbarButton
                label={t('knowledge.soul.save')}
                variant="default"
                onClick={save}
                disabled={saving || status.isOver || !dirty}
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </ToolbarButton>
            </>
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="relative flex min-w-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : shownVersion ? (
            <>
              <div className="flex items-center justify-between gap-3 border-b border-dashed px-6 py-2 text-xs text-muted-foreground">
                <span>
                  {t('knowledge.soul.version', { version: shownVersion.version })} · {formatDateTimeBR(shownVersion.created_at)}
                </span>
                {isAdmin && (
                  <Button type="button" size="sm" variant="outline" onClick={() => restore(shownVersion.version)} disabled={restoring}>
                    {restoring ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-2 h-3.5 w-3.5" />}
                    {t('knowledge.soul.restore')}
                  </Button>
                )}
              </div>
              <div className="px-6 py-5">
                <div className="text-sm leading-6">
                  <InlineMarkdownField value={shownVersion.content} readOnly view="rendered" />
                </div>
              </div>
            </>
          ) : editing && view === 'code' ? (
            <InlineMarkdownField value={draft} onChange={setDraft} view="code" editorHeight={SOUL_EDITOR_HEIGHT} />
          ) : (
            <div className="px-6 pb-8 pt-5">
              {shownText.trim() ? (
                <div className="text-sm leading-6">
                  <InlineMarkdownField value={shownText} readOnly view="rendered" />
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                  {isAdmin ? t('knowledge.soul.emptyAdmin') : t('knowledge.soul.empty')}
                </div>
              )}
            </div>
          )}
        </div>
        {/* O contador fica no canto inferior direito do campo, como num textarea. */}
        <span
          className={cn(
            'pointer-events-none absolute bottom-1.5 right-3 rounded bg-background/80 px-1 text-[11px]',
            status.isOver ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {status.isOver
            ? t('common.markdown.overLimit', { count: status.over })
            : t('common.markdown.characters', { count: status.count, max: status.max })}
        </span>
        </div>

        {historyOpen && !editing && (
          <SoulHistoryList
            versions={versions}
            isLoading={versionsLoading}
            selected={selectedVersion}
            onSelect={setSelectedVersion}
          />
        )}
      </div>
    </Card>
  );
};

/** Botão de ícone da barra, com a dica no hover — o mesmo padrão da Mind. */
const ToolbarButton = ({
  label,
  onClick,
  disabled,
  variant = 'ghost',
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'ghost' | 'secondary' | 'default';
  children: ReactNode;
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" variant={variant} size="sm" className="h-8 w-8 p-0" aria-label={label} onClick={onClick} disabled={disabled}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs">{label}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);
